'use client';

import React from 'react';
import { Flex, Text, Box, VStack, HStack, Button, Image } from '@skin-seoul/design-system';
import { LucideIcon } from '../LucideIcon';
import { OrderTrackingInfo } from './OrderTrackingInfo';

interface OrderItem {
  id: number;
  name: string;
  imageUrl?: string;
  variant: string;
  quantity: number;
  price: number;
}

interface TrackingEvent {
  id: string;
  type: string;
  title: string;
  timestamp: string;
  status: 'completed' | 'current' | 'pending';
}

interface OrderCardProps {
  order: {
    id: number;
    orderNumber: string;
    status: string;
    orderDate: string;
    deliveryDate: string;
    trackingNumber: string;
    total: number;
    currency: string;
    items: OrderItem[];
    trackingEvents: TrackingEvent[];
  };
}

const DELIVERY_STATUS_COLOR = {
  delivered: 'success.600',
  shipped: 'primary.500',
  processing: 'warning.500',
  pending: 'neutral.400',
} as const;

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const OrderCard = ({ order }: OrderCardProps) => {
  const statusColor = DELIVERY_STATUS_COLOR[order.status as keyof typeof DELIVERY_STATUS_COLOR] || 'neutral.400';

  return (
    <VStack
      width="100%"
      bg="generic.0"
      border="1px solid"
      borderColor="neutral.100"
      borderRadius="8px"
      overflow="hidden"
      gap={0}
    >
      <VStack gap={3} padding={3} width="100%" alignItems="flex-start">
        <Flex justifyContent="space-between" alignItems="flex-end" width="100%">
          <VStack gap={1} alignItems="flex-start">
            <HStack gap={2}>
              <Box width={2} height={2} borderRadius="50%" bg={statusColor} />
              <Text textStyle="heading.h5.mobile" fontWeight="bold" color="neutral.600" textTransform="capitalize">
                {order.status}
              </Text>
            </HStack>
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Ordered on {order.orderDate}
            </Text>
          </VStack>
          <Text
            textStyle="label.small"
            fontWeight="medium"
            color="neutral.400"
            textDecoration="underline"
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
          >
            Receipt
          </Text>
        </Flex>

        <Flex
          bg="primary.50"
          border="1px solid"
          borderColor="primary.100"
          borderRadius="8px"
          padding={3}
          justifyContent="space-between"
          width="100%"
        >
          <VStack gap={1} alignItems="flex-start" flex="1">
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Order Number
            </Text>
            <HStack gap={1}>
              <Text textStyle="label.medium" fontWeight="medium" color="neutral.700">
                {order.orderNumber}
              </Text>
              <Box
                cursor="pointer"
                onClick={() => copyToClipboard(order.orderNumber)}
                _hover={{ opacity: 0.8 }}
                width={4}
                height={4}
              >
                <LucideIcon name="Copy" width={4} height={4} />
              </Box>
            </HStack>
          </VStack>
          <VStack gap={1} alignItems="flex-end">
            <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
              Total
            </Text>
            <Text textStyle="label.medium" fontWeight="medium" color="neutral.700">
              {order.total}
            </Text>
          </VStack>
        </Flex>

        {order.items.map((item) => (
          <HStack key={item.id} gap={3} width="100%">
            <Box
              width="72px"
              height="72px"
              border="1px solid"
              borderColor="neutral.50"
              borderRadius="8px"
              overflow="hidden"
              flexShrink={0}
            >
              <Box width="64px" height="64px" borderRadius="8px" overflow="hidden">
                <Image src={item.imageUrl} alt={item.name} width="100%" height="100%" objectFit="contain" />
              </Box>
            </Box>

            <VStack gap={3} flex="1" alignItems="flex-start">
              <VStack gap={1} width="100%" alignItems="flex-start">
                <Text textStyle="label.small" fontWeight="semibold" color="neutral.700">
                  {item.name}
                </Text>
                <HStack justifyContent="space-between" width="100%">
                  <HStack gap={1}>
                    <Text textStyle="paragraph.xsmall" fontWeight="regular" color="neutral.500">
                      {item.variant}
                    </Text>
                    <Text textStyle="paragraph.xsmall" fontWeight="regular" color="neutral.500">
                      Qty:{item.quantity}
                    </Text>
                  </HStack>
                  <Text textStyle="paragraph.xsmall" fontWeight="regular" color="neutral.500">
                    {item.price}
                  </Text>
                </HStack>
              </VStack>
              <Button
                variant="outline"
                size="sm"
                bg="neutral.50"
                borderColor="neutral.100"
                color="neutral.500"
                textStyle="paragraph.xsmall"
                fontWeight="regular"
                paddingX={3}
                paddingY={1}
                height={7}
                borderRadius="4px"
                _hover={{ bg: 'neutral.100' }}
              >
                Write Review
              </Button>
            </VStack>
          </HStack>
        ))}

        <OrderTrackingInfo
          deliveryDate={order.deliveryDate}
          trackingNumber={order.trackingNumber}
          trackingEvents={order.trackingEvents}
        />
      </VStack>
    </VStack>
  );
};

